/**
 * Lazy loading and image performance optimization service
 */

class ImagePerformanceService {
  private imageCache = new Map<string, boolean>();
  private observer: IntersectionObserver | null = null;

  constructor() {
    this.initIntersectionObserver();
  }

  /**
   * Initialize the IntersectionObserver for lazy loading
   */
  private initIntersectionObserver() {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            this.loadImage(img);
            this.observer?.unobserve(img);
          }
        });
      },
      {
    rootMargin: '50px 0px', // Start loading 50px before the image is visible
        threshold: 0.1
      }
    );
  }

  /**
   * Set up lazy loading for an image
   */
  setupLazyLoading(img: HTMLImageElement) {
    if (!this.observer) {
      this.loadImage(img);
      return;
    }

  // Store the original URL
    const dataSrc = img.dataset.src;
    if (dataSrc) {
      img.src = this.createPlaceholder(img.offsetWidth, img.offsetHeight);
      this.observer.observe(img);
    }
  }

  /**
   * Load an image in an optimized way
   */
  private loadImage(img: HTMLImageElement) {
    const dataSrc = img.dataset.src;
    if (!dataSrc) return;

    const cacheKey = dataSrc;
    
  // Check the cache
    if (this.imageCache.has(cacheKey)) {
      img.src = dataSrc;
      img.classList.add('loaded');
      return;
    }

  // Preload the image
    const imageLoader = new Image();
    imageLoader.onload = () => {
      img.src = dataSrc;
      img.classList.add('loaded');
      this.imageCache.set(cacheKey, true);
    };
    
    imageLoader.onerror = () => {
      img.classList.add('error');
    };
    
    imageLoader.src = dataSrc;
  }

  /**
   * Create an SVG placeholder to reduce CLS
   */
  private createPlaceholder(width: number, height: number): string {
    const svg = `
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f3f4f6"/>
        <text x="50%" y="50%" font-family="Arial" font-size="14" fill="#9ca3af" text-anchor="middle" dy=".3em">
    Loading...
        </text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  /**
   * Preload critical images
   */
  preloadCriticalImages(urls: string[]) {
    if (typeof window === 'undefined') return;

    urls.forEach(url => {
      if (this.imageCache.has(url)) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
      
      this.imageCache.set(url, true);
    });
  }

  /**
   * Choose optimal image format (WebP if supported)
   */
  getOptimalFormat(): 'webp' | 'jpg' {
    if (typeof window === 'undefined') return 'jpg';
    
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    
    try {
      return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0 ? 'webp' : 'jpg';
    } catch {
      return 'jpg';
    }
  }

  /**
   * Calculate optimal dimensions to avoid CLS
   */
  calculateAspectRatio(originalWidth: number, originalHeight: number, maxWidth: number): {
    width: number;
    height: number;
  } {
    const aspectRatio = originalHeight / originalWidth;
    const width = Math.min(originalWidth, maxWidth);
    const height = Math.round(width * aspectRatio);
    
    return { width, height };
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.imageCache.clear();
  }
}

export const imagePerformanceService = new ImagePerformanceService();
export default imagePerformanceService;
